import { useState, useEffect } from 'react';
import api from '../../lib/axios';
import AttachmentList from '../email/AttachmentList';

export default function EmailViewerModal({ emailId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!emailId) return;

    // cancelled evita dos problemas reales: (1) abrir un correo lento, cerrarlo
    // y abrir otro hacía que al resolver el primero se pisara el contenido y el
    // modal mostrara el correo equivocado; (2) el error de un intento anterior
    // quedaba pegado para siempre porque nunca se limpiaba al reintentar.
    let cancelled = false;

    const fetchEmail = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/api/emails/${emailId}`);
        if (!cancelled) setData(response.data);
      } catch (err) {
        console.error('Error fetching email details:', err);
        if (!cancelled) setError('No se pudo cargar el contenido del correo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchEmail();

    return () => { cancelled = true; };
  }, [emailId]);

  if (!emailId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 bg-slate-50 dark:bg-slate-800/50">
          <h2 className="text-lg font-semibold truncate flex-1">
            {loading ? 'Cargando correo...' : data?.email?.subject || 'Sin asunto'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg text-gray-500 transition-colors ml-4"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-slate-900">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-40 space-y-3">
              <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
              <p className="text-sm text-gray-500">Descargando contenido desde el servidor de correo...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
               <span className="material-symbols-outlined mr-2">error</span>
               {error}
            </div>
          ) : data ? (
            <>
              {/* Metadata */}
              <div className="mb-6 space-y-2 text-sm border-b border-gray-100 dark:border-gray-800 pb-4">
                <div className="flex items-start">
                  <span className="font-semibold text-gray-600 dark:text-gray-400 w-16">De:</span>
                  <span className="text-gray-800 dark:text-gray-200 break-all">{data.email.from_email}</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold text-gray-600 dark:text-gray-400 w-16">Para:</span>
                  <span className="text-gray-800 dark:text-gray-200 break-all">{data.email.to_email}</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold text-gray-600 dark:text-gray-400 w-16">Fecha:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {new Date(data.email.date).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Attachments */}
              {data.content.attachments && data.content.attachments.length > 0 && (
                <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800/30 rounded-lg">
                  <h3 className="text-sm font-semibold mb-3 flex items-center text-slate-700 dark:text-slate-300">
                    <span className="material-symbols-outlined text-[18px] mr-2">attachment</span>
                    Archivos Adjuntos ({data.content.attachments.length})
                  </h3>
                  <AttachmentList attachments={data.content.attachments} />
                </div>
              )}

              {/* HTML Body Render in an iframe to prevent CSS bleeding */}
              <div className="w-full min-h-[400px] border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden relative bg-white">
                <iframe
                  title="email-content"
                  className="w-full h-full min-h-[400px]"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="utf-8">
                        <base target="_blank">
                        <style>
                          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; line-height: 1.5; color: #333; padding: 1rem; }
                          a { color: #2563eb; text-decoration: none; }
                          a:hover { text-decoration: underline; }
                          img { max-width: 100%; height: auto; }
                        </style>
                      </head>
                      <body>${data.content.html || '<em>Sin contenido HTML</em>'}</body>
                    </html>
                  `}
                />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
